/**
 * A team's auto-join email domains as a staff form holds them: one
 * comma-separated field (the create dialog and the Email domains page).
 * The API normalizes again and refuses a public provider, so this turns
 * the field into the list, and back, and catches a domain the API would
 * refuse as malformed before the save.
 */

/** "Acme.io, @acme.com" to ['acme.io', 'acme.com']; a repeat is dropped. */
export function parseDomains(raw: string): string[] {
  const domains: string[] = []
  for (const part of raw.split(',')) {
    const domain = part.trim().toLowerCase().replace(/^@/, '')
    if (domain.length > 0 && !domains.includes(domain)) domains.push(domain)
  }
  return domains
}

/** The field's text for a saved list. */
export function formatDomains(domains: string[]): string {
  return domains.join(', ')
}

/** Order-insensitive: the API stores a set of domains. */
export function sameDomains(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((domain) => b.includes(domain))
}

/**
 * A DNS name: two or more dot-separated labels of lowercase letters,
 * digits and inner hyphens, at most 253 characters. The API's allowlist
 * (`_DOMAIN_PATTERN` in api/app/schemas/internal.py), so the form refuses
 * what the save would.
 */
const DOMAIN_PATTERN = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/
const DOMAIN_MAX_LENGTH = 253

/** The first entry the API would refuse as malformed, or null when every entry is a domain. */
export function malformedDomain(domains: string[]): string | null {
  return (
    domains.find((domain) => domain.length > DOMAIN_MAX_LENGTH || !DOMAIN_PATTERN.test(domain)) ??
    null
  )
}

/** Staff's typing on the Email domains page, with the saved set it started from. */
export interface DomainsEdit {
  text: string
  startedFrom: string[]
  /**
   * A save of this edit failed in a way that may have stored it (a 5xx
   * or a lost response). The api stores the set before it adds existing
   * users, so a resend is how that backfill finishes, and the edit stays
   * sendable even once the saved set equals it.
   */
  resend: boolean
}

/** Whether Save has something to send: an edit that differs from the saved set, or a resend. */
export function editHasChanges(edit: DomainsEdit | null, saved: string[]): boolean {
  if (edit === null) return false
  return edit.resend || !sameDomains(parseDomains(edit.text), saved)
}
